export type AccountStatus =
  'provisioning' | 'onboarding' | 'active' | 'provisioning_failed' | 'disabled' | 'archived';
export type LoginRole = 'owner' | 'admin' | 'staff';
export interface PermissionDefinition {
  key: string;
  label: string;
  module: string;
  minimumRole: 'staff' | 'admin' | 'owner';
  dependencies: string[];
}
const definitions: PermissionDefinition[] = [];
function modulePermissions(
  module: string,
  actions: Array<[string, string, ('staff' | 'admin' | 'owner')?]>,
) {
  for (const [action, label, minimumRole] of actions)
    definitions.push({
      key: `${module}.${action}`,
      label,
      module,
      minimumRole: minimumRole ?? 'staff',
      dependencies: action === 'view' ? [] : [`${module}.view`],
    });
}
modulePermissions('dashboard', [['view', 'View dashboard']]);
modulePermissions('products', [
  ['view', 'View products'],
  ['create', 'Create products', 'admin'],
  ['edit', 'Edit products', 'admin'],
  ['delete', 'Deactivate products', 'admin'],
  ['import', 'Import products', 'admin'],
]);
modulePermissions('inventory', [
  ['view', 'View stock'],
  ['opening', 'Opening stock', 'admin'],
  ['adjust', 'Adjust stock', 'admin'],
]);
for (const name of ['customers', 'suppliers'])
  modulePermissions(name, [
    ['view', `View ${name}`],
    ['create', 'Create'],
    ['edit', 'Edit'],
    ['delete', 'Remove', 'admin'],
  ]);
modulePermissions('sales', [
  ['view', 'View sales'],
  ['create', 'Create sales'],
  ['edit', 'Edit sales'],
  ['status', 'Fulfillment status'],
  ['collect', 'Collect payments'],
  ['correct', 'Correct payments'],
  ['refund', 'Refund', 'admin'],
  ['return', 'Return workflow'],
  ['delete', 'Delete sales', 'admin'],
  ['whatsapp', 'Share sale with customer on WhatsApp'],
]);
definitions.push(
  {
    key: 'sales_contribution.view_own',
    label: 'View My Sales',
    module: 'sales_contribution',
    minimumRole: 'staff',
    dependencies: ['sales.view'],
  },
  {
    key: 'sales_contribution.view_all',
    label: 'View Team Sales',
    module: 'sales_contribution',
    minimumRole: 'admin',
    dependencies: ['sales.view'],
  },
  {
    key: 'sales_contribution.export',
    label: 'Export authorized sales tracking',
    module: 'sales_contribution',
    minimumRole: 'staff',
    dependencies: ['sales_contribution.view_own'],
  },
);
modulePermissions('purchases', [
  ['view', 'View purchases', 'admin'],
  ['create', 'Create purchases', 'admin'],
  ['edit', 'Edit purchases', 'admin'],
  ['receive', 'Receive stock', 'admin'],
  ['pay', 'Pay suppliers', 'admin'],
  ['correct', 'Correct supplier payments', 'admin'],
  ['cancel', 'Cancel unreceived purchases', 'admin'],
  ['status', 'Update purchase delivery', 'admin'],
  ['delete', 'Move mistaken purchases to bin', 'admin'],
  ['restore', 'Restore purchase records', 'admin'],
  ['archive', 'Archive genuine purchases', 'admin'],
]);
for (const name of ['overview', 'cashbook', 'expenses', 'balances', 'capital'])
  modulePermissions(`accounts_${name}`, [
    ['view', `View ${name}`, 'admin'],
    ['manage', `Manage ${name}`, 'admin'],
  ]);
modulePermissions('plans', [
  ['view', 'View all plans and tasks', 'admin'],
  ['manage', 'Manage plans/tasks and assign work', 'admin'],
]);
modulePermissions('my_tasks', [
  ['view', 'View own tasks'],
  ['update', 'Update own progress'],
  ['comment', 'Comment on own tasks'],
]);
for (const name of [
  'sales_summary',
  'sales_by_product',
  'stock_valuation',
  'receivables',
  'purchases',
  'returns',
])
  modulePermissions(`report_${name}`, [
    ['view', `View ${name.replaceAll('_', ' ')} report`, 'admin'],
    ['export', 'Export report', 'admin'],
  ]);
modulePermissions('master_config', [
  ['view', 'View master configuration', 'admin'],
  ['manage', 'Manage master configuration', 'admin'],
]);
modulePermissions('audit', [['view', 'View audit history', 'admin']]);
modulePermissions('recycle', [
  ['view', 'View recycle bin', 'admin'],
  ['restore', 'Restore records', 'admin'],
  ['purge', 'Permanently delete eligible records', 'admin'],
]);
modulePermissions('imports', [
  ['view', 'View data imports', 'admin'],
  ['manage', 'Commit imports', 'admin'],
]);
definitions.push({
  key: 'financials.view',
  label: 'View confidential costs, profit and balances',
  module: 'financials',
  minimumRole: 'admin',
  dependencies: [],
});
for (const kind of ['products', 'customers', 'suppliers', 'sellers', 'accounts'])
  definitions.push({
    key: `lookup.${kind}`,
    label: `${kind} lookup for forms`,
    module: 'lookup',
    minimumRole: 'staff',
    dependencies: [],
  });
const extraDependencies: Record<string, string[]> = {
  'purchases.view': ['financials.view'],
  'products.import': ['imports.manage'],
  'sales.refund': ['lookup.accounts', 'financials.view'],
  'products.create': ['financials.view'],
  'products.edit': ['financials.view'],
  'products.delete': ['financials.view'],
  'inventory.opening': ['financials.view'],
  'inventory.adjust': ['financials.view'],
  'plans.manage': ['lookup.sellers'],
  'recycle.view': ['financials.view'],
  'imports.view': ['financials.view'],
  'audit.view': ['financials.view'],
  'report_sales_summary.view': ['sales.view'],
  'report_sales_by_product.view': ['sales.view', 'products.view'],
  'report_returns.view': ['sales.view'],
  'report_stock_valuation.view': ['inventory.view'],
  'report_purchases.view': ['purchases.view'],
  'report_receivables.view': ['sales.view'],
  'sales.create': ['lookup.products', 'lookup.customers', 'lookup.sellers'],
  'sales.edit': ['lookup.products', 'lookup.customers', 'lookup.sellers'],
  'sales.collect': ['lookup.accounts'],
  'sales.correct': ['lookup.accounts'],
  'purchases.create': ['lookup.suppliers', 'financials.view'],
  'purchases.edit': ['lookup.suppliers', 'financials.view'],
  'purchases.pay': ['lookup.accounts', 'financials.view'],
  'purchases.correct': ['lookup.accounts', 'financials.view'],
  'purchases.cancel': ['financials.view'],
};
for (const definition of definitions) {
  definition.dependencies.push(...(extraDependencies[definition.key] ?? []));
  if (definition.module.startsWith('accounts_') || definition.module.startsWith('report_'))
    definition.dependencies.push('financials.view');
}
export const PERMISSION_CATALOG: readonly PermissionDefinition[] = definitions;
export const OWN_TASK_PERMISSIONS = ['my_tasks.view', 'my_tasks.update', 'my_tasks.comment'];
export const ACCESS_PRESETS = [
  { id: 'own_tasks', name: 'Assigned tasks only', permissions: OWN_TASK_PERMISSIONS },
  {
    id: 'sales_staff',
    name: 'Sales and stock',
    permissions: [
      ...OWN_TASK_PERMISSIONS,
      'products.view',
      'inventory.view',
      'customers.view',
      'customers.create',
      'customers.edit',
      'sales.view',
      'sales.create',
      'sales.edit',
      'sales.status',
      'sales.collect',
      'sales.correct',
      'sales.return',
    ],
  },
  {
    id: 'operations_admin',
    name: 'Operations admin',
    permissions: [
      ...OWN_TASK_PERMISSIONS,
      'products.view',
      'products.create',
      'products.edit',
      'inventory.view',
      'inventory.adjust',
      'customers.view',
      'suppliers.view',
      'sales.view',
      'sales.create',
      'sales.edit',
      'sales.status',
      'sales.collect',
      'plans.view',
      'plans.manage',
      'master_config.view',
    ],
  },
] as const;
export interface PermissionIssue {
  /** The requested grant that cannot be assigned. */
  key: string;
  /** The grant or dependency responsible for the failure. */
  dependency: string;
  code: 'unknown_permission' | 'role_restricted' | 'cyclic_dependency';
  message: string;
}

/** Inspect without throwing so stale grants cannot crash an access form. */
export function inspectPermissions(keys: readonly string[], role: LoginRole) {
  const permissions = new Set<string>();
  const issues: PermissionIssue[] = [];
  const levels = { staff: 0, admin: 1, owner: 2 };
  for (const root of new Set(keys)) {
    const resolved = new Set<string>();
    const visiting = new Set<string>();
    function visit(key: string): PermissionIssue | undefined {
      const definition = PERMISSION_CATALOG.find((item) => item.key === key);
      if (!definition)
        return {
          key: root,
          dependency: key,
          code: 'unknown_permission',
          message: `Unrecognized privilege: ${key}. Review it before saving.`,
        };
      if (levels[role] < levels[definition.minimumRole])
        return {
          key: root,
          dependency: key,
          code: 'role_restricted',
          message: `${definition.label} requires ${roleLabel(definition.minimumRole)} access.`,
        };
      if (visiting.has(key))
        return {
          key: root,
          dependency: key,
          code: 'cyclic_dependency',
          message: `The privilege configuration for ${definition.label} contains a dependency cycle.`,
        };
      if (resolved.has(key)) return;
      visiting.add(key);
      for (const dependency of definition.dependencies) {
        const issue = visit(dependency);
        if (issue) return issue;
      }
      visiting.delete(key);
      resolved.add(key);
    }
    const issue = visit(root);
    if (issue) issues.push(issue);
    else resolved.forEach((key) => permissions.add(key));
  }
  return { permissions: [...permissions].sort(), issues };
}

export function resolvePermissions(keys: string[], role: LoginRole): string[] {
  const inspected = inspectPermissions(keys, role);
  const issue = inspected.issues[0];
  if (issue) throw new Error(issue.message);
  return inspected.permissions;
}
export interface AccessContext {
  ready: boolean;
  profile_id: string;
  user_id: string;
  organization_id: string;
  role: LoginRole;
  status: AccountStatus;
  must_change_password: boolean;
  onboarding_expired: boolean;
  session_valid: boolean;
  access_version: number;
  profile_version: number;
  permissions: string[];
}
export function canAccess(context: AccessContext | null, permission: string): boolean {
  return !!context?.ready && (context.role === 'owner' || context.permissions.includes(permission));
}
export function roleLabel(role?: string | null) {
  return role === 'owner' ? 'Super Admin' : role === 'admin' ? 'Admin' : 'Staff';
}
export function permissionForPath(path: string): string | undefined {
  if (path === '/deliveries' || path.startsWith('/deliveries/')) return 'sales.view';
  if (path === '/print-templates' || path.startsWith('/print-templates/')) return 'sales.view';
  if (path.startsWith('/my-sales')) return 'sales_contribution.view_own';
  if (path.startsWith('/team-sales')) return 'sales_contribution.view_all';
  if (path.startsWith('/settings/users')) return 'owner';
  if (path.startsWith('/my-tasks')) return 'my_tasks.view';
  if (path.startsWith('/plans')) return 'plans.view';
  if (path.startsWith('/master-config')) return 'master_config.view';
  if (path.startsWith('/recycle-bin')) return 'recycle.view';
  if (path === '/products/import') return 'products.import';
  if (path.startsWith('/settings/import') || path.includes('/import')) return 'imports.view';
  if (path.startsWith('/audit')) return 'audit.view';
  if (path.startsWith('/payments')) return 'accounts_expenses.manage';
  if (path.startsWith('/accounts')) {
    const tab = path.split('/')[2] ?? 'overview';
    return `accounts_${['cashbook', 'expenses', 'balances', 'capital'].includes(tab) ? tab : 'overview'}.view`;
  }
  const reports: Record<string, string> = {
    'sales/summary': 'sales_summary',
    'sales/by-product': 'sales_by_product',
    'inventory/valuation': 'stock_valuation',
    'financial/receivables-aging': 'receivables',
    'purchases/summary': 'purchases',
    'returns/summary': 'returns',
  };
  if (path.startsWith('/reports/'))
    return `report_${reports[path.slice(9)] ?? 'sales_summary'}.view`;
  if (path === '/reports') return 'reports';
  for (const resource of ['products', 'inventory', 'customers', 'suppliers', 'sales', 'purchases'])
    if (path.startsWith(`/${resource}`))
      return `${resource}.${path.endsWith('/new') ? 'create' : path.endsWith('/edit') ? 'edit' : 'view'}`;
  return path === '/' ? 'dashboard.view' : undefined;
}
export function landingPath(context: AccessContext): string {
  const paths = [
    '/',
    '/my-tasks',
    '/sales',
    '/products',
    '/inventory',
    '/customers',
    '/suppliers',
    '/purchases',
    '/plans',
    '/accounts',
    '/master-config',
  ];
  return (
    paths.find((path) => canAccess(context, permissionForPath(path)!)) ?? '/access-unavailable'
  );
}
